/**
 * App.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { PlanView } from '../views/PlanView.js'
import { GasesView } from '../views/GasesView.js'
import { SettingsView } from '../views/SettingsView.js'
import { ProfileView } from '../views/ProfileView.js'
import { SavedPlansView } from '../views/SavedPlansView.js'
import { DashboardView } from '../views/DashboardView.js'
import { DayView } from '../views/DayView.js'
import { SupportView } from '../views/SupportView.js'
import { i18n, languages } from '../i18n/index.js'

export class App {
  constructor(container, router) {
    this.container = container
    this.router = router
    this.views = {
      '/': PlanView,
      '/gases': GasesView,
      '/settings': SettingsView,
      '/profile': ProfileView,
      '/saved': SavedPlansView,
      // Full width, like /saved and /settings: the series is a screen of its
      // own and never shares the desktop dashboard with the plan
      '/day': DayView,
      // Reached from the settings screen and from the foot of the dashboard,
      // deliberately absent from both navigation bars: the diver came here to
      // plan a dive, not to be asked for money on every screen
      '/support': SupportView
    }
    // Routes that show dashboard on desktop
    this.dashboardRoutes = ['/', '/gases', '/profile']
    // Detect if running as native app (Capacitor)
    this.isNative = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()
    // Currently mounted view instance, destroyed before mounting the next one
    this.currentView = null
    // First launch language selector, kept so it can be removed from the DOM
    this.languageModal = null
    this.languageModalKeyHandler = null
  }

  // Check if we're on desktop (width >= 1024px)
  isDesktop() {
    return window.innerWidth >= 1024
  }

  t(key) {
    return i18n.t(key)
  }

  getCurrentTheme() {
    return localStorage.getItem('mvplan_theme') || 'dark'
  }

  toggleTheme() {
    const newTheme = this.getCurrentTheme() === 'dark' ? 'light' : 'dark'
    localStorage.setItem('mvplan_theme', newTheme)
    document.documentElement.setAttribute('data-theme', newTheme)
    this.updateThemeIcons()
  }

  getThemeIcon() {
    const isDark = this.getCurrentTheme() === 'dark'
    if (isDark) {
      // Sun icon - click to switch to light
      return `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>`
    } else {
      // Moon icon - click to switch to dark
      return `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>`
    }
  }

  updateThemeIcons() {
    const icon = this.getThemeIcon()
    const mobileBtn = document.getElementById('theme-toggle-mobile')
    const desktopBtn = document.getElementById('theme-toggle-desktop')
    if (mobileBtn) mobileBtn.innerHTML = icon
    if (desktopBtn) desktopBtn.innerHTML = icon
  }

  mount() {
    this.render()

    // Register routes
    Object.keys(this.views).forEach(path => {
      this.router.register(path, () => this.renderView(path))
    })

    // Check if first launch (no language selected yet)
    if (!localStorage.getItem('mvplan_language_selected')) {
      this.showLanguageSelector()
    }

    this.router.start()
  }

  showLanguageSelector() {
    // Never stack two selectors if this is called again
    this.closeLanguageSelector()

    const modal = document.createElement('div')
    modal.id = 'language-modal'
    modal.className = 'fixed inset-0 bg-black/70 flex items-center justify-center z-50'
    modal.innerHTML = `
      <div class="bg-base-100 rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
        <h2 class="text-xl font-bold text-center mb-6">Select your language</h2>
        <div class="grid grid-cols-3 gap-4">
          ${languages.map(lang => `
            <button class="lang-btn flex flex-col items-center p-4 rounded-lg hover:bg-base-200 transition-colors" data-lang="${lang.code}">
              <span class="text-4xl mb-2">${lang.flag}</span>
              <span class="text-sm">${lang.name}</span>
            </button>
          `).join('')}
        </div>
      </div>
    `

    document.body.appendChild(modal)
    this.languageModal = modal

    // Handle language selection using event delegation
    modal.addEventListener('click', (e) => {
      const btn = e.target.closest('.lang-btn')
      if (!btn) {
        // Click on the dark backdrop itself (outside the dialog box) dismisses
        if (e.target === modal) this.closeLanguageSelector()
        return
      }

      const langCode = btn.dataset.lang
      if (!langCode) {
        console.error('App.showLanguageSelector: language button without data-lang, selection ignored')
        return
      }

      // Save selection
      localStorage.setItem('mvplan_language_selected', 'true')
      localStorage.setItem('mvplan_locale', langCode)

      // Update i18n, drop the modal, then reload page to apply language
      i18n.setLocale(langCode)
      this.closeLanguageSelector()
      window.location.reload()
    })

    // Escape dismisses the selector
    this.languageModalKeyHandler = (e) => {
      if (e.key === 'Escape') this.closeLanguageSelector()
    }
    document.addEventListener('keydown', this.languageModalKeyHandler)
  }

  // Remove the language modal and its keyboard listener from the document.
  // mvplan_language_selected is deliberately left untouched: dismissing the
  // selector without choosing keeps it for the next launch.
  closeLanguageSelector() {
    if (this.languageModalKeyHandler) {
      document.removeEventListener('keydown', this.languageModalKeyHandler)
      this.languageModalKeyHandler = null
    }
    if (this.languageModal) {
      this.languageModal.remove()
      this.languageModal = null
    }
  }

  render() {
    this.container.innerHTML = `
      <div class="flex flex-col lg:flex-row h-screen safe-area-top safe-area-bottom">
        <!-- Desktop sidebar (hidden on mobile). Each entry carries its label:
             the bare icons left the Day screen undiscoverable, since a tooltip
             only shows once the pointer is already on the right button. -->
        <aside class="hidden lg:flex lg:flex-col lg:w-20 bg-base-100 border-r border-base-300">
          <div class="p-2 border-b border-base-300 flex justify-center">
            <span class="text-lg font-bold">mv</span>
          </div>
          <nav class="flex-1 flex flex-col items-center py-4 gap-1 px-1">
            <a data-route="/" class="btn btn-ghost flex-col flex-nowrap h-auto min-h-0 w-full gap-1 px-1 py-2" title="${this.t('nav_plan')}">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span class="text-[10px] font-normal leading-tight text-center normal-case">${this.t('nav_plan')}</span>
            </a>
            <a data-route="/day" class="btn btn-ghost flex-col flex-nowrap h-auto min-h-0 w-full gap-1 px-1 py-2" title="${this.t('nav_day')}">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span class="text-[10px] font-normal leading-tight text-center normal-case">${this.t('nav_day')}</span>
            </a>
            <a data-route="/saved" class="btn btn-ghost flex-col flex-nowrap h-auto min-h-0 w-full gap-1 px-1 py-2" title="${this.t('nav_saved')}">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              <span class="text-[10px] font-normal leading-tight text-center normal-case">${this.t('nav_saved')}</span>
            </a>
            <a data-route="/settings" class="btn btn-ghost flex-col flex-nowrap h-auto min-h-0 w-full gap-1 px-1 py-2" title="${this.t('nav_settings')}">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span class="text-[10px] font-normal leading-tight text-center normal-case">${this.t('nav_settings')}</span>
            </a>
          </nav>
                    <div class="p-2 border-t border-base-300 flex flex-col items-center gap-1">
            <button class="btn btn-ghost btn-square" id="theme-toggle-desktop" title="${this.t('settings_theme')}">
              ${this.getThemeIcon()}
            </button>
          </div>
          ${!this.isNative ? `
          <div class="p-2 border-t border-base-300 flex flex-col items-center gap-1">
            <a href="https://mvplan.org/v2026/itunes" target="_blank" rel="noopener" class="btn btn-ghost btn-xs btn-square" title="${this.t('app_get_ios')}">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
            </a>
            <a href="https://mvplan.org/v2026/gplay" target="_blank" rel="noopener" class="btn btn-ghost btn-xs btn-square" title="${this.t('app_get_android')}">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
            </a>
          </div>
          ` : ''}
        </aside>

        <!-- Main column (header + content + bottom nav on mobile) -->
        <div class="flex flex-col flex-1 min-w-0">
          <!-- Header (mobile only, desktop has sidebar) -->
          <header class="navbar bg-base-100 border-b border-base-300 px-2 lg:hidden">
            <div class="flex-1">
              <span class="text-xl font-bold pl-2">mvplan</span>
            </div>
            <div class="flex-none flex items-center gap-2">
              ${!this.isNative ? `
              <span class="text-xs opacity-70 hidden sm:inline">${this.t('app_available_on')}</span>
              <a href="https://mvplan.org/v2026/itunes" target="_blank" rel="noopener" class="btn btn-ghost btn-xs px-1" title="${this.t('app_get_ios')}">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </a>
              <a href="https://mvplan.org/v2026/gplay" target="_blank" rel="noopener" class="btn btn-ghost btn-xs px-1" title="${this.t('app_get_android')}">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
              </a>
              ` : ''}
              <button class="btn btn-ghost btn-circle" id="theme-toggle-mobile" title="${this.t('settings_theme')}">
                ${this.getThemeIcon()}
              </button>
              <button class="btn btn-ghost btn-circle" id="settings-btn">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
          </header>

          <!-- Main content -->
          <main id="view-container" class="flex-1 overflow-y-auto p-4 lg:p-6">
          </main>

          <!-- Bottom navigation (mobile only) -->
          <nav class="btm-nav btm-nav-sm bg-base-100 border-t border-base-300 lg:hidden">
            <button data-route="/gases">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              <span class="btm-nav-label">${this.t('nav_gases')}</span>
            </button>
            <button data-route="/">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span class="btm-nav-label">${this.t('nav_plan')}</span>
            </button>
            <button data-route="/profile">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
              <span class="btm-nav-label">${this.t('nav_profile')}</span>
            </button>
            <button data-route="/day">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span class="btm-nav-label">${this.t('nav_day')}</span>
            </button>
            <button data-route="/saved">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              <span class="btm-nav-label">${this.t('nav_saved')}</span>
            </button>
          </nav>
        </div>
      </div>
    `

    // Setup nav clicks
    this.container.querySelectorAll('[data-route]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.router.navigate(btn.dataset.route)
        this.updateNav(btn.dataset.route)
      })
    })

    // Settings button
    document.getElementById('settings-btn')?.addEventListener('click', () => {
      this.router.navigate('/settings')
      this.updateNav('/settings')
    })

    // Theme toggle buttons
    document.getElementById('theme-toggle-mobile')?.addEventListener('click', () => this.toggleTheme())
    document.getElementById('theme-toggle-desktop')?.addEventListener('click', () => this.toggleTheme())
  }

  updateNav(route) {
    // Update bottom nav (mobile)
    this.container.querySelectorAll('nav.btm-nav [data-route]').forEach(btn => {
      const isActive = btn.dataset.route === route
      btn.classList.toggle('active', isActive)
      btn.classList.toggle('text-primary', isActive)
    })
    // Update sidebar nav (desktop). A DaisyUI button reads 'btn-active', not
    // 'active': with the latter alone the current screen was highlighted
    // nowhere on desktop.
    this.container.querySelectorAll('aside [data-route]').forEach(link => {
      const isActive = link.dataset.route === route
      link.classList.toggle('btn-active', isActive)
      link.classList.toggle('text-primary', isActive)
    })
  }

  renderView(path) {
    const container = document.getElementById('view-container')
    if (!container) {
      console.error(`App.renderView: #view-container is missing, route ${path} not mounted`)
      return
    }

    // Release the previous view (store subscriptions, document listeners,
    // pending timers) before building the next one
    this.destroyCurrentView()

    // Drop the previous markup so the new view starts from an empty container
    container.innerHTML = ''

    // On desktop, use DashboardView for main routes (gases, plan, profile)
    const ViewClass = this.isDesktop() && this.dashboardRoutes.includes(path)
      ? DashboardView
      : this.views[path]

    if (!ViewClass) {
      console.error(`App.renderView: no view registered for route ${path}, nothing mounted`)
      this.updateNav(path)
      return
    }

    // Store the instance before render() so a view that fails mid-render is
    // still destroyed on the next navigation
    const view = new ViewClass(container)
    this.currentView = view
    view.render()

    this.updateNav(path)
  }

  // Destroy the mounted view. Views are not required to expose destroy(), and
  // a failing teardown must never prevent the next view from mounting.
  destroyCurrentView() {
    const view = this.currentView
    this.currentView = null
    if (!view || typeof view.destroy !== 'function') return

    try {
      view.destroy()
    } catch (err) {
      console.error('App.destroyCurrentView: view teardown failed', err)
    }
  }
}
